import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Grid, Icon, IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import { usePermission } from "auth/usePermission";
import {useAuth} from "../../auth/Auth";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PearlLogoLoader from "../../components/PearlLogoLoader";
import EmptyState from 'components/EmptyTable/EmptyState';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { useActiveApp } from "shell-v2/apps";

const formatAmount = (amount, currency) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: currency, maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

function ActionMenu({ row, onView, onEdit, onDelete }) {
    const [anchorEl, setAnchorEl] = useState(null);
    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {
                    onView &&
                    <MenuItem onClick={() => { onView(row); setAnchorEl(null); }}>
                        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                        View
                    </MenuItem>
                }
                {
                    onEdit &&
                    <MenuItem onClick={() => { onEdit(row); setAnchorEl(null); }}>
                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                        Edit
                    </MenuItem>
                }
                {
                    onDelete &&
                    <MenuItem onClick={() => { onDelete(row.id); setAnchorEl(null); }} sx={{ color: "error.main" }}>
                        <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
                        Delete
                    </MenuItem>
                }
            </Menu>
        </>
    );
}

function TravelOrders() {

  const { axiosInstance } = useAuth();
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // then the table in one card.
  const isV2 = useTheme()?.pearlUi === "v2";
  const { app } = useActiveApp();
  const ordersLabel = app?.labels?.["travel-orders"] || "Orders";
  const navigate = useNavigate();
  const { canCreate } = usePermission('travel_orders');

  // Initializing states

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success",
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
      open: false,
      id: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

    // GET order request on first render

  useEffect(() => {
      fetchOrders();
  }, [])

  const fetchOrders = async () => {
      setIsLoading(true);
      try {
          const response = await axiosInstance.get('/travel-orders/');
          setOrders(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
          console.log('Error retrieving orders', error);
          setSnackbar({open: true, message: 'Error loading orders', severity: 'error'})
      } finally {
          setIsLoading(false);
      }
  };

    // DELETE selected order

    const handleDeleteClick = (id) => {
        setDeleteConfirm({open: true, id: id})
    }

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await axiosInstance.delete(`/travel-orders/${deleteConfirm.id}`);
            setSnackbar({open: true, message: 'Order deleted successfully', severity: 'success'})
            await fetchOrders();
        } catch (error) {
            setSnackbar({open: true, message: 'Error deleting order', severity: 'error'})
        } finally {
            setIsDeleting(false);
            setDeleteConfirm({open: false, id: null})
        }
    }

  const handleCreateOrder = () => {
    navigate("/travel-orders/new");
  };

  const handleViewOrder = (order) => {
    navigate(`/travel-order/${order.id}`);
  };

  const handleEditOrder = (order) => {
      navigate(`/travel-orders/${order.id}/edit`);
  };

  // Preparing table columns

  const columns = useMemo(
    () => [
      {
        Header: "Order ID",
        accessor: "id",
        width: "10%",
        Cell: ({ value, row }) => (
          <MDTypography
            sx={{ cursor: "pointer" }}
            onClick={() => handleViewOrder(row.original)}
          >
              <MonoText component="span" onClick={() => handleViewOrder(row.original)} color="info" sx={{ cursor: 'pointer' }}>
                  #{value}
              </MonoText>
          </MDTypography>
        ),
      },
      {
        Header: "Customer Name",
        accessor: "account_name",
        width: "18%",
        Cell: ({ value, row }) => (
          <MDTypography
              sx={{ cursor: "pointer" }}
          >
              <MonoText
                  component="span"
                  onClick={() => navigate(`/travel-order-pax/${row.original.primary_contact_id}`)}
                  color="info"
                  sx={{ cursor: 'pointer' }}
              >
                  {value}
              </MonoText>
          </MDTypography>
        ),
      },
      {
        Header: "Package",
        accessor: "package",
        width: "18%",
        Cell: ({ value, row }) => (
            <MDTypography
                sx={{ cursor: "pointer" }}
            >
                <MonoText
                    component="span"
                    onClick={() => navigate(`/trip-packages/detail/${row.original.trip_package_id}`)}
                    color="info"
                    sx={{ cursor: 'pointer' }}
                >
                    {value}
                </MonoText>
            </MDTypography>
        ),
      },
      {
        Header: "Order Date",
        accessor: "order_date",
        width: "14%",
        Cell: ({ value }) => <MonoText>{formatDate(value)}</MonoText>,
      },
      {
        Header: "Order Amount",
        accessor: "order_amount",
        width: "14%",
        Cell: ({ value, row }) => (
          <MDTypography variant="caption" fontWeight="bold">
            {formatAmount(value, row.original.currency)}
          </MDTypography>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        width: "12%",
          Cell: ({ value }) => {
              const words = value?.trim().split(/\s+/) || [];
              const displayValue = words.length > 1
                  ? words.slice(1).join(" ")
                  : value;

              return (
                  <StatusBadge
                      size="xs"
                      status={value}
                      label={displayValue}
                  />
              );
          },
      },
      {
        Header: "Created By",
        accessor: "created_by_name",
        width: "18%",
        Cell: ({ value, row }) => (
            <MDTypography
                sx={{ cursor: "pointer" }}
            >
                <MonoText
                    component="span"
                    onClick={() => navigate(`/users/${row.original.created_by}/profile`)}
                    color="info"
                    sx={{ cursor: 'pointer' }}
                >
                    {value}
                </MonoText>
            </MDTypography>
        ),
      },
      {
        Header: "Action",
        accessor: "actions",
        width: "8%",
        disableSortBy: true,
          Cell: ({ row }) => (
              <ActionMenu
                  row={row.original}
                  onView={handleViewOrder}
                  onDelete={isDeleting ? undefined : handleDeleteClick}
                  onEdit={handleEditOrder}
              />
          ),
      },
    ],
    []
  );

  // Preparing table

  const tableData = useMemo(
    () => ({
      columns,
      rows: orders,
    }),
    [columns, orders]
  );

  // Rendering component

  // Shared by the classic and v2 layouts below: the same actions and table.
  const listActions = (
    <>
      <MDButton variant="gradient" color="dark" onClick={handleCreateOrder} disabled={!canCreate}>
        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
        &nbsp;Create New Order
      </MDButton>
    </>
  );
  const listBody = (
    <>
      {isLoading ? (
          <MDBox py={6} display="flex" justifyContent="center">
              <PearlLogoLoader size="md" message="Loading data..." />
          </MDBox>
      ) : orders?.length > 0 ? (
          <DataTable
              table={tableData}
              isSorted
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              canSearch
              showTotalEntries
              noEndBorder
              objectType="travel_orders"
              onFilterClear={fetchOrders}
          />
      ) : (
          <EmptyState moduleName="Order"/>
      )
      }
    </>
  );

  const v2Main = (
    <MDBox>
      <PageHeader title={ordersLabel} summary={isLoading ? null : `${orders?.length || 0} ${(orders?.length || 0) === 1 ? "booking" : ordersLabel.toLowerCase()}`} actions={listActions} />
      <Card>
        <MDBox pt={1}>{listBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Orders
                  </MDTypography>
                  <MDTypography variant="caption" color="white" opacity={0.85}>
                    Travel order bookings and reservations
                  </MDTypography>
                </MDBox>
                {listActions}
              </MDBox>
              <MDBox pt={3}>
                  {listBody}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      )}

        {/* Snackbar : success/error messages */}

        <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Alert
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                {snackbar.message}
            </Alert>
        </Snackbar>

        {/* Delete Confirmation Dialog */}

        <Dialog
            open={deleteConfirm.open}
            onClose={() => !isDeleting && setDeleteConfirm({ open: false, id: null })}
        >
            <DialogTitle>Delete Order</DialogTitle>
            <DialogContent>
                <Typography>Are you sure you want to delete this Order? This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => setDeleteConfirm({ open: false, id: null })}
                    disabled={isDeleting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleDelete}
                    color="error"
                    variant="contained"
                    disabled={isDeleting}
                >
                    {isDeleting ? "Deleting..." : "Delete"}
                </Button>
            </DialogActions>
        </Dialog>

    </DashboardLayout>
  );
}

export default TravelOrders;
